import React, { useEffect } from 'react';
import { Card, Typography } from 'antd';
import { CheckCircleFilled, HomeOutlined, ShoppingOutlined } from '@ant-design/icons';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { clearCart } from '../../redux/slices/shoppingCartSlice';
import { fetchCartItemsCount } from '../../services';
import './reviewConfirmPage.scss';

const { Title, Text } = Typography;

const ReviewConfirmPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const user = useAppSelector((state) => state.user.user);
  const paymentReferenceId = location.state?.paymentReferenceId;
  const paymentConfirmed =
    location.state?.paymentConfirmed === true &&
    typeof paymentReferenceId === 'string' &&
    paymentReferenceId.trim().length > 0;
  const paymentMethod = location.state?.paymentMethod || 'your payment provider';
  const isAuthenticatedOrder =
    typeof location.state?.isAuthenticatedOrder === 'boolean'
      ? location.state.isAuthenticatedOrder
      : Boolean(user?.id);

  useEffect(() => {
    if (!paymentConfirmed) {
      return;
    }

    dispatch(clearCart());

    if (user?.id) {
      void dispatch(fetchCartItemsCount(user.id));
    }
  }, [dispatch, paymentConfirmed, user?.id]);

  if (!paymentConfirmed) {
    return <Navigate to="/cart" replace />;
  }

  return (
    <div className="review-confirm-container">
      <Card className="review-confirm-card">
        <div className="review-confirm-badge">
          {paymentConfirmed ? 'Payment Received' : 'Payment Status'}
        </div>
        <CheckCircleFilled className="review-confirm-icon" />

        <div className="review-confirm-text">
          <Title level={2}>Thank you for your order</Title>
          <Text>
            {paymentConfirmed
              ? `Your ${paymentMethod} payment was successful and your order is now being prepared.`
              : 'We are confirming your payment. Refresh your order history in a moment if it is not listed yet.'}
          </Text>
        </div>

        <div className="review-confirm-grid">
          <div className="review-confirm-panel">
            <h4>Status</h4>
            <p>{paymentConfirmed ? 'Paid successfully' : 'Confirmation pending'}</p>
            <span>{paymentConfirmed ? 'We will start processing your order shortly.' : 'Do not submit payment again while confirmation is in progress.'}</span>
          </div>
          <div className="review-confirm-panel">
            <h4>What next</h4>
            <p>{isAuthenticatedOrder ? 'Track it in your account' : 'Your order is confirmed'}</p>
            <span>
              {isAuthenticatedOrder
                ? 'You can review your order history and shipping progress there.'
                : 'You can continue shopping or return to the home page.'}
            </span>
          </div>
        </div>

        <div className="review-confirm-buttons">
          {isAuthenticatedOrder ? (
            <Link
              to="/UserPage/order-history"
              className="ant-btn ant-btn-primary ant-btn-lg"
            >
              <ShoppingOutlined aria-hidden="true" />
              <span>View Orders</span>
            </Link>
          ) : (
            <Link
              to="/products/types/Rings"
              className="ant-btn ant-btn-primary ant-btn-lg"
            >
              <ShoppingOutlined aria-hidden="true" />
              <span>Continue Shopping</span>
            </Link>
          )}
          <Link to="/" className="ant-btn ant-btn-default ant-btn-lg">
            <HomeOutlined aria-hidden="true" />
            <span>Back Home</span>
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default ReviewConfirmPage;
